import { useMutation, useQueryClient } from 'react-query';
import {
  ACCESS_TOKEN_KEY,
  fetchToken,
  FetchTokenDataType,
  FetchTokenReturnType,
  logoutUser,
  REFRESH_TOKEN_KEY
} from '../../api/auth';
import { APIErrorType } from '../../api';
import { CreateStudentDataType, StudentType } from '../../api/student';
import { CreateUserDataType } from '../../api/user';
import { useCreateUserMutation } from './use-user';
import { useCreateStudentMutation } from './use-student';

export function useLoginMutation() {
  const queryClient = useQueryClient();

  async function onSuccess({ access, refresh }: { access: string; refresh: string }) {
    await localStorage.setItem(ACCESS_TOKEN_KEY, access);
    await localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
    await queryClient.invalidateQueries('user');
  }

  return useMutation<FetchTokenReturnType, APIErrorType, FetchTokenDataType>(fetchToken, {
    onSuccess
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();

  async function onSuccess() {
    await localStorage.removeItem(ACCESS_TOKEN_KEY);
    await localStorage.removeItem(REFRESH_TOKEN_KEY);
    await queryClient.invalidateQueries();
  }

  return useMutation(logoutUser, { onSuccess });
}

export type UseRegisterMutationDataType = CreateUserDataType & CreateStudentDataType;

export function useRegisterMutation() {
  const { mutateAsync: createUser } = useCreateUserMutation();
  const { mutateAsync: createStudent } = useCreateStudentMutation();
  const { mutateAsync: fetchToken } = useLoginMutation();

  async function registerUser(data: UseRegisterMutationDataType) {
    const { notify, ...userData } = data;
    await createUser(userData);
    const student = await createStudent({ notify });
    const { mobile, password } = userData;
    await fetchToken({ mobile, password });
    return student;
  }

  return useMutation<StudentType, APIErrorType, UseRegisterMutationDataType>(registerUser);
}
